/**
 * Settings Modal - Sophisticated popup settings panel
 *
 * Clean, floating modal with live preview of changes.
 * Uses CSS custom properties for real-time theme updates.
 */

import { useState } from 'react'
import * as Switch from '@radix-ui/react-switch'
import * as Dialog from '@radix-ui/react-dialog'
import { X, Sun, Moon, Monitor, Type, Eye, Palette, Check, Settings2, Accessibility, Bell } from 'lucide-react'
import { useTheme } from '../providers/ThemeProvider'
import {
  useSettingsStore,
  COLOR_THEMES,
  type FontSize,
  type ContrastMode,
  type ColorTheme,
} from '../stores/settingsStore'
import { cn } from '../lib/utils'

interface SettingsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type SettingsTab = 'appearance' | 'accessibility' | 'preferences'

export default function SettingsModal({ open, onOpenChange }: SettingsModalProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>('appearance')
  const { theme, setTheme } = useTheme()
  const {
    colorTheme,
    reducedMotion,
    notificationsEnabled,
    analyticsEnabled,
    showIconLabels,
    highContrast,
    fontSize,
    setColorTheme,
    toggleReducedMotion,
    toggleNotifications,
    toggleAnalytics,
    toggleIconLabels,
    setHighContrast,
    setFontSize,
  } = useSettingsStore()

  const tabs: { id: SettingsTab; label: string; icon: typeof Settings2 }[] = [
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'accessibility', label: 'Accessibility', icon: Accessibility },
    { id: 'preferences', label: 'Preferences', icon: Bell },
  ]

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50 animate-in fade-in duration-200"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50',
            'w-full max-w-2xl max-h-[85vh] overflow-hidden',
            'rounded-2xl shadow-2xl',
            'animate-in fade-in zoom-in-95 duration-200',
            'flex'
          )}
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          }}
        >
          {/* Sidebar Tabs */}
          <div
            className="w-52 p-4 flex flex-col gap-1"
            style={{
              background: 'linear-gradient(180deg, var(--color-bg-tertiary) 0%, var(--color-bg-secondary) 100%)',
              borderRight: '1px solid var(--color-border-subtle)',
            }}
          >
            <Dialog.Title
              className="text-lg font-semibold mb-4 px-3 flex items-center gap-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              <Settings2 className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
              Settings
            </Dialog.Title>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all duration-200 flex items-center gap-2.5'
                )}
                style={{
                  backgroundColor: activeTab === tab.id ? 'var(--color-accent-muted)' : 'transparent',
                  color: activeTab === tab.id ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                }}
              >
                <tab.icon className="w-4 h-4" />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            <Dialog.Close asChild>
              <button
                className="absolute top-4 right-4 p-2 rounded-lg transition-colors"
                style={{ color: 'var(--color-text-muted)' }}
              >
                <X className="w-5 h-5" />
              </button>
            </Dialog.Close>

            {activeTab === 'appearance' && (
              <AppearanceTab
                theme={theme}
                setTheme={setTheme}
                colorTheme={colorTheme}
                setColorTheme={setColorTheme}
              />
            )}

            {activeTab === 'accessibility' && (
              <AccessibilityTab
                showIconLabels={showIconLabels}
                toggleIconLabels={toggleIconLabels}
                fontSize={fontSize}
                setFontSize={setFontSize}
                highContrast={highContrast}
                setHighContrast={setHighContrast}
                reducedMotion={reducedMotion}
                toggleReducedMotion={toggleReducedMotion}
              />
            )}

            {activeTab === 'preferences' && (
              <PreferencesTab
                notificationsEnabled={notificationsEnabled}
                toggleNotifications={toggleNotifications}
                analyticsEnabled={analyticsEnabled}
                toggleAnalytics={toggleAnalytics}
              />
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3
      className="text-xs font-semibold uppercase tracking-wider mb-4"
      style={{ color: 'var(--color-text-muted)' }}
    >
      {children}
    </h3>
  )
}

function AppearanceTab({
  theme,
  setTheme,
  colorTheme,
  setColorTheme,
}: {
  theme: string
  setTheme: (t: 'light' | 'dark' | 'system') => void
  colorTheme: ColorTheme
  setColorTheme: (t: ColorTheme) => void
}) {
  return (
    <div className="space-y-8">
      {/* Theme Mode */}
      <section>
        <SectionTitle>Theme Mode</SectionTitle>
        <div className="flex gap-3">
          {[
            { value: 'light', icon: Sun, label: 'Light' },
            { value: 'dark', icon: Moon, label: 'Dark' },
            { value: 'system', icon: Monitor, label: 'System' },
          ].map(({ value, icon: Icon, label }) => (
            <button
              key={value}
              onClick={() => setTheme(value as 'light' | 'dark' | 'system')}
              className="flex-1 flex flex-col items-center gap-2 py-4 rounded-xl transition-all duration-200"
              style={{
                backgroundColor: theme === value ? 'var(--color-accent-muted)' : 'var(--color-bg-tertiary)',
                color: theme === value ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                border: theme === value ? '1px solid var(--color-accent)' : '1px solid transparent',
              }}
            >
              <Icon className="w-5 h-5" />
              <span className="text-sm font-medium">{label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Color Theme */}
      <section>
        <SectionTitle>Color Theme</SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          {(Object.entries(COLOR_THEMES) as [ColorTheme, typeof COLOR_THEMES[ColorTheme]][]).map(
            ([key, value]) => (
              <button
                key={key}
                onClick={() => setColorTheme(key)}
                className="relative flex items-center gap-3 p-4 rounded-xl transition-all duration-200 text-left group"
                style={{
                  backgroundColor: value.bgSecondary,
                  border: colorTheme === key
                    ? `2px solid ${value.accent}`
                    : '2px solid transparent',
                }}
              >
                {/* Color Preview Swatches */}
                <div className="flex gap-0.5 flex-shrink-0">
                  <div
                    className="w-3 h-10 rounded-l-md"
                    style={{ backgroundColor: value.bg }}
                  />
                  <div
                    className="w-3 h-10"
                    style={{ backgroundColor: value.bgTertiary }}
                  />
                  <div
                    className="w-3 h-10 rounded-r-md"
                    style={{ backgroundColor: value.accent }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-sm font-medium"
                    style={{ color: value.textPrimary }}
                  >
                    {value.label}
                  </p>
                  <p
                    className="text-xs truncate"
                    style={{ color: value.textMuted }}
                  >
                    {value.description}
                  </p>
                </div>
                {colorTheme === key && (
                  <div
                    className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: value.accent }}
                  >
                    <Check className="w-3 h-3" style={{ color: value.bg }} />
                  </div>
                )}
              </button>
            )
          )}
        </div>
      </section>
    </div>
  )
}

function AccessibilityTab({
  showIconLabels,
  toggleIconLabels,
  fontSize,
  setFontSize,
  highContrast,
  setHighContrast,
  reducedMotion,
  toggleReducedMotion,
}: {
  showIconLabels: boolean
  toggleIconLabels: () => void
  fontSize: FontSize
  setFontSize: (s: FontSize) => void
  highContrast: ContrastMode
  setHighContrast: (m: ContrastMode) => void
  reducedMotion: boolean
  toggleReducedMotion: () => void
}) {
  return (
    <div className="space-y-6">
      {/* Icon Labels */}
      <SettingRow
        label="Show icon labels"
        description="Display text labels next to navigation icons"
        checked={showIconLabels}
        onChange={toggleIconLabels}
      />

      {/* Font Size */}
      <div className="py-2">
        <div className="mb-3">
          <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
            Font size
          </span>
          <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
            Adjust text size for readability
          </p>
        </div>
        <div className="flex gap-2">
          {([
            { value: 'normal', label: 'Normal', size: 'text-xs' },
            { value: 'large', label: 'Large', size: 'text-sm' },
            { value: 'xl', label: 'Extra Large', size: 'text-base' },
          ] as { value: FontSize; label: string; size: string }[]).map(({ value, label, size }) => (
            <button
              key={value}
              onClick={() => setFontSize(value)}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg transition-all duration-200"
              style={{
                backgroundColor: fontSize === value ? 'var(--color-accent-muted)' : 'var(--color-bg-tertiary)',
                color: fontSize === value ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                border: fontSize === value ? '1px solid var(--color-accent)' : '1px solid transparent',
              }}
            >
              <Type
                className={cn(
                  value === 'normal' && 'w-3.5 h-3.5',
                  value === 'large' && 'w-4 h-4',
                  value === 'xl' && 'w-5 h-5'
                )}
              />
              <span className={size}>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Contrast */}
      <div className="py-2">
        <div className="mb-3">
          <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
            Contrast
          </span>
          <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
            Increase visual contrast for better visibility
          </p>
        </div>
        <div className="flex gap-2">
          {([
            { value: 'normal', label: 'Normal' },
            { value: 'high', label: 'High' },
            { value: 'highest', label: 'Highest' },
          ] as { value: ContrastMode; label: string }[]).map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setHighContrast(value)}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg transition-all duration-200"
              style={{
                backgroundColor: highContrast === value ? 'var(--color-accent-muted)' : 'var(--color-bg-tertiary)',
                color: highContrast === value ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                border: highContrast === value ? '1px solid var(--color-accent)' : '1px solid transparent',
              }}
            >
              <Eye className="w-4 h-4" />
              <span className="text-xs font-medium">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Reduce Motion */}
      <SettingRow
        label="Reduce motion"
        description="Minimize animations throughout the interface"
        checked={reducedMotion}
        onChange={toggleReducedMotion}
      />
    </div>
  )
}

function PreferencesTab({
  notificationsEnabled,
  toggleNotifications,
  analyticsEnabled,
  toggleAnalytics,
}: {
  notificationsEnabled: boolean
  toggleNotifications: () => void
  analyticsEnabled: boolean
  toggleAnalytics: () => void
}) {
  return (
    <div className="space-y-4">
      <SettingRow
        label="Notifications"
        description="Receive alerts for important updates"
        checked={notificationsEnabled}
        onChange={toggleNotifications}
      />
      <SettingRow
        label="Analytics"
        description="Help improve LUKHAS with anonymous usage data"
        checked={analyticsEnabled}
        onChange={toggleAnalytics}
      />
    </div>
  )
}

function SettingRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string
  description?: string
  checked: boolean
  onChange: () => void
}) {
  return (
    <div
      className="flex items-center justify-between py-3 px-4 rounded-lg"
      style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
    >
      <div>
        <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
          {label}
        </span>
        {description && (
          <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
            {description}
          </p>
        )}
      </div>
      <Switch.Root
        checked={checked}
        onCheckedChange={onChange}
        className="w-11 h-6 rounded-full transition-colors duration-200"
        style={{
          backgroundColor: checked ? 'var(--color-accent)' : 'var(--color-bg-secondary)',
          border: checked ? 'none' : '1px solid var(--color-border)',
        }}
      >
        <Switch.Thumb
          className={cn(
            'block w-4 h-4 rounded-full transition-transform duration-200',
            'translate-x-1 data-[state=checked]:translate-x-6'
          )}
          style={{
            backgroundColor: checked ? 'var(--color-bg)' : 'var(--color-text-muted)',
          }}
        />
      </Switch.Root>
    </div>
  )
}
